import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { 
  Plus, 
  Edit, 
  Trash2,
  FolderOpen,
  BookOpen
} from 'lucide-react';
import { categoriesApi } from '@/services/api';
import { Category } from '@/types';
import { toast } from 'sonner';

const categorySchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  description: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

const predefinedColors = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
  '#F97316', '#6366F1', '#14B8A6', '#F43F5E'
];

const predefinedIcons = [
  'ri-code-s-slash-line',
  'ri-brain-line',
  'ri-robot-line',
  'ri-megaphone-line',
  'ri-share-line',
  'ri-shuffle-line',
  'ri-palette-line',
  'ri-camera-line',
  'ri-music-line',
  'ri-heart-line',
  'ri-star-line',
  'ri-fire-line'
];

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await categoriesApi.getAll();
      if ((response.data as any).success) {
        setCategories((response.data as any).data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar las categorías');
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: CategoryFormData) => {
    try {
      if (editingCategory) {
        await categoriesApi.update(editingCategory.id, data);
        toast.success('Categoría actualizada exitosamente');
      } else {
        await categoriesApi.create(data);
        toast.success('Categoría creada exitosamente');
      }
      
      setIsModalOpen(false);
      setEditingCategory(null);
      reset();
      
      // Refrescar la lista desde el servidor
      await fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar la categoría');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    reset({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (category: Category) => {
    if (category._count?.courses && category._count.courses > 0) {
      toast.error('No se puede eliminar una categoría que tiene cursos asociados');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?`)) return;
    
    try {
      await categoriesApi.delete(category.id);
      toast.success('Categoría eliminada exitosamente');
      // Refrescar la lista desde el servidor
      await fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar la categoría');
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    reset({
      color: predefinedColors[0],
      icon: predefinedIcons[0],
    });
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-32 bg-gray-200 rounded"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Categorías</h1>
          <p className="text-gray-600">Organiza los cursos por categorías temáticas</p>
        </div>
        <Button onClick={openCreateModal} leftIcon={Plus}>
          Nueva Categoría
        </Button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(categories || []).map((category) => (
          <Card key={category.id} className="hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: category.color || '#3B82F6' }}
              >
                {category.icon ? (
                  <i className={`${category.icon} text-xl`}></i>
                ) : (
                  <FolderOpen className="h-6 w-6" />
                )}
              </div>
              <div className="flex space-x-1">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDelete(category)}
                  disabled={Boolean(category._count?.courses && category._count.courses > 0)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {category.name}
              </h3>
              {category.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {category.description}
                </p>
              )}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-1" />
                  {category._count?.courses || 0} cursos
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  category.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {category.isActive ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <Card className="text-center py-12">
          <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay categorías creadas
          </h3>
          <p className="text-gray-600 mb-6">
            Comienza creando categorías para organizar tus cursos
          </p>
          <Button onClick={openCreateModal} leftIcon={Plus}>
            Crear Primera Categoría
          </Button>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingCategory ? 'Editar Categoría' : 'Crear Nueva Categoría'}
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  {...register('name')}
                  label="Nombre de la categoría"
                  placeholder="Ej: Inteligencia Artificial"
                  error={errors.name?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe la categoría..."
                  />
                </div>

                {/* Color Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {(predefinedColors || []).map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 ${
                          selectedColor === color ? 'border-gray-400' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setValue('color', color)}
                      />
                    ))}
                  </div>
                </div>

                {/* Icon Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icono
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {(predefinedIcons || []).map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        className={`w-8 h-8 rounded border flex items-center justify-center ${
                          selectedIcon === icon 
                            ? 'border-primary-500 bg-primary-50' 
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setValue('icon', icon)}
                      >
                        <i className={`${icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vista previa
                  </label>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: selectedColor || '#3B82F6' }}
                    >
                      {selectedIcon ? (
                        <i className={`${selectedIcon} text-lg`}></i>
                      ) : (
                        <FolderOpen className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {watch('name') || 'Nombre de la categoría'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {watch('description') || 'Descripción de la categoría'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    isLoading={isSubmitting}
                  >
                    {editingCategory ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};